import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import { ShieldCheck, TrendingUp, FileText, Bell, ArrowRight, Sparkles } from "lucide-react";

const HERO = "https://images.pexels.com/photos/36729739/pexels-photo-36729739.jpeg";
const WORKSHOP = "https://images.unsplash.com/photo-1597960194599-22929afc25b1";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 stagger">
            <div className="label-caps mb-6">Monitoraggio IVA · Piccole imprese italiane</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-[#1E3F33]">
              Sai già oggi quanta IVA<br/>dovrai versare a fine trimestre.
            </h1>
            <p className="mt-8 text-lg text-[#2C2C2C] max-w-xl leading-relaxed">
              IVA Radar stima in anticipo il tuo debito IVA in base ai movimenti reali e al tuo settore. Importi le fatture con un click da CSV o XML e vedi subito quanta IVA accantonare per il prossimo versamento.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/registrati" data-testid="hero-cta-signup" className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24]">
                Prova IVA Radar <ArrowRight size={16} />
              </Link>
              <Link to="/piani" data-testid="hero-cta-pricing" className="btn-lift inline-flex items-center gap-2 px-6 py-3 border border-[#1E3F33] text-[#1E3F33] rounded-md hover:bg-[#EBE5DF]">
                Vedi i piani
              </Link>
            </div>
            <div className="mt-10 max-w-lg">
              <Disclaimer compact />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-md border border-[#EBE5DF] shadow-sm">
              <img src={HERO} alt="Piccolo imprenditore" className="w-full h-full object-cover"/>
              <div className="absolute bottom-4 left-4 right-4 bg-[#F9F8F6]/95 px-4 py-3 rounded-md border border-[#EBE5DF]">
                <div className="flex items-center justify-between">
                  <div className="label-caps text-[#C25934]">Stima prossima scadenza</div>
                  <span className="text-[10px] uppercase tracking-widest text-[#6B6B6B] bg-[#EBE5DF] px-2 py-0.5 rounded">Esempio</span>
                </div>
                <div className="font-serif-display text-3xl text-[#1E3F33] mt-1">€ 4.820,00</div>
                <div className="text-xs text-[#6B6B6B]">dato dimostrativo · saldo IVA a debito</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-t border-[#EBE5DF] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, title: "Stima anticipata", desc: "Vedi il saldo IVA in tempo reale, aggiornato ad ogni nuova fattura." },
            { icon: FileText, title: "Import CSV & XML SDI", desc: "Carichi i movimenti in pochi secondi da CSV o direttamente dalle fatture elettroniche." },
            { icon: Bell, title: "Alert intelligenti", desc: "Ricevi avvisi quando il debito supera la soglia che decidi tu." },
            { icon: ShieldCheck, title: "A supporto del tuo commercialista", desc: "Uno strumento in più per pianificare la liquidità, non per sostituire l'adempimento." },
          ].map((f, i) => (
            <div key={i} className="card-flat p-6 btn-lift" data-testid={`feature-${i}`}>
              <f.icon className="text-[#C25934]" size={22}/>
              <h3 className="mt-4 font-serif-display text-2xl text-[#1E3F33]">{f.title}</h3>
              <p className="mt-2 text-sm text-[#6B6B6B] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHO */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="aspect-[5/4] overflow-hidden rounded-md border border-[#EBE5DF]">
            <img src={WORKSHOP} alt="Artigiano al lavoro" className="w-full h-full object-cover"/>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="label-caps mb-4">A chi è dedicato</div>
          <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3F33]">Pensato per chi ogni giorno tiene aperta un'attività reale.</h2>
          <ul className="mt-8 space-y-3 text-[#2C2C2C]">
            {[
              "Ristoranti, bar, pizzerie",
              "Officine meccaniche e carrozzerie",
              "Negozi al dettaglio e commercianti",
              "Rappresentanti e agenti di commercio",
              "Imprese edili e artigiani",
              "Liberi professionisti e studi",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C25934]" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#EBE5DF]">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3F33]">Inizia con un check singolo da 29 €</h2>
          <p className="mt-4 text-[#6B6B6B]">Un report IVA istantaneo per capire subito a che punto sei.</p>
          <Link to="/piani" data-testid="cta-bottom-pricing" className="btn-lift inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24]">
            Scopri i piani <ArrowRight size={16}/>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#EBE5DF] py-8">
        <div className="max-w-7xl mx-auto px-6 text-xs text-[#6B6B6B] flex flex-wrap gap-2 justify-between">
          <span>© {new Date().getFullYear()} IVA Radar · Strumento di stima, non sostitutivo del commercialista.</span>
          <span>Made in Italy</span>
        </div>
      </footer>
    </div>
  );
}
